// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {IIntegration, ISophosTenantsResponse, ITenant, PartnerTypes} from 'src/types/integrations';
import {ActivatedRoute, Router} from '@angular/router';
import {D3eService} from 'src/services/d3e-service';
import {HeaderService} from 'src/app/header.service';
import {NotificationService} from 'src/services/notification-service';
import {MatDialog} from '@angular/material/dialog';
import {AlertDialogComponent} from 'src/app/dialogs/alert/alert.component';
import {SelectedOrgMethod} from '../../../../shared/selectedOrg';
import {TranslateService} from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-sophos',
    templateUrl: './sophos.component.html',
    styleUrls: ['./sophos.component.scss'],
    standalone: false
})
export class SophosComponent implements OnInit {

  private readonly PARTNER_ID = 3;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  tenants: ITenant[] = [];
  loading = true;

  selectedOrgId: string;
  hasUserBeenSet = false;

  fDirections: string;
  fCID: string;
  fCIDInvalid: string;
  fCIDPHolder: string;
  fSecret: string;
  fTenant: string;
  fGetTenant: string;
  fLockA: string;
  fLockB: string;
  fLockC: string;
  fAdmin: string;
  fCancel: string;
  fSave: string;
  fConfigSaved: string;
  fChangesBeingSaved: string;
  fSaveError: string;
  fErrorFetch: string;

  title: string;
  subtitle: string;
  constructor(
    private route: ActivatedRoute,
    private d3eService: D3eService,
    private router: Router,
    private headerService: HeaderService,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.translate.get(['INTS_DIRECTIONS', 'INTS_CID', 'INTS_CID_INVALID', 'INTS_CID_PHOLDER', 'INTS_SECRET', 'INTS_TENANT',
      'INTS_GET_TENANT', 'INTS_LOCKA', 'INTS_LOCKB', 'INTS_LOCKC', 'INTS_ADMIN', 'CANCEL', 'SAVE', 'TITLE_SOPHOS_INT',
      'SUBTITLE_SOPHOS', 'CONFIG_SAVED', 'CHANGES_BEING_SAVED', 'REPORT_SAVE_ERROR', 'INTS_ERROR_FETCH']).subscribe(vals => {
          this.fDirections = vals['INTS_DIRECTIONS'];
          this.fCID = vals['INTS_CID'];
          this.fCIDInvalid = vals['INTS_CID_INVALID'];
          this.fCIDPHolder = vals['INTS_CID_PHOLDER'];
          this.fSecret = vals['INTS_SECRET'];
          this.fTenant = vals['INTS_TENANT'];
          this.fGetTenant = vals['INTS_GET_TENANT'];
          this.fLockA = vals['INTS_LOCKA'];
          this.fLockB = vals['INTS_LOCKB'];
          this.fLockC = vals['INTS_LOCKC'];
          this.fAdmin = vals['INTS_ADMIN'];
          this.fCancel = vals['CANCEL'];
          this.fSave = vals['SAVE'];
          this.fConfigSaved = vals['CONFIG_SAVED']
          this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];
          this.fSaveError = vals['REPORT_SAVE_ERROR'];
          this.fErrorFetch = vals['INTS_ERROR_FETCH'];

          this.title = vals['TITLE_SOPHOS_INT'];
          this.subtitle = vals['SUBTITLE_SOPHOS'];

          this.headerService.setTitle(this.title);
          this.headerService.setSubtitle(this.subtitle);
        });
    this.headerService.setIcon('mdi-sync');

    this.formGroup = this.formBuilder.group({
      client_id: ['', Validators.compose([Validators.minLength(36), Validators.required,
          Validators.pattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)])],
      client_secret: ['', Validators.required],
      tenant_id: ['', Validators.required],
      lock_option: ['', Validators.required],
      admin_unlock: [false],
    });
  }

  ngOnInit() {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        this.formGroup.controls.lock_option.setValue('device_only');
        this.loading = false;
      } else {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrg)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['client_id'].setValue(integration.config.client_id);
            this.formGroup.controls['client_secret'].setValue(integration.config.client_secret);
            this.formGroup.controls['tenant_id'].setValue(integration.config.tenant_id);
            this.formGroup.controls['lock_option'].setValue(integration.config.lock_option);
            this.formGroup.controls['admin_unlock'].setValue(integration.config.admin_unlock);
            this.integration = integration;
            this.getTenants();
          });
      }
    });
  }

  save() {
    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        partner_id: PartnerTypes.SOPHOS,
        config: {
          client_id: data.client_id,
          client_secret: data.client_secret,
          tenant_id: data.tenant_id,
          api_url: this.getApiHost(data.tenant_id),
          admin_unlock: data.admin_unlock,
          lock_option: data.lock_option,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(result => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.config = {
        client_id: data.client_id,
        client_secret: data.client_secret,
        tenant_id: data.tenant_id,
        api_url: this.getApiHost(data.tenant_id),
        admin_unlock: data.admin_unlock,
        lock_option: data.lock_option,
    };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  getApiHost(tenantId: string): string {
    const tenant = this.tenants.find(t => t.id === tenantId);
    return tenant.apiHost;
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigSaved);
  }

  reportSaveError() {
    this.notification.showError(this.fSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const vals = this.translate.instant(['DISCARD_CHANGES', 'DISCARD_CHANGES_CONTENT', 'NO', 'YES']);
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: vals['DISCARD_CHANGES'],
          content: vals['DISCARD_CHANGES_CONTENT'],
          rejectBtnName: vals['NO'],
          acceptBtnName: vals['YES'],
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  getTenants() {
    const data = this.formGroup.value;
    this.d3eService.get_sophos_tenants(data.client_id, data.client_secret)
      .pipe(untilDestroyed(this))
      .subscribe((result: ISophosTenantsResponse) => {
          if (result.status === 'OK') {
            result.tenants.sort((a: ITenant, b: ITenant) => (a.name < b.name) ? -1 : 1);
            this.tenants = result.tenants;
          } else {
            this.notification.showError(this.translate.instant('REPORT_RESULT_ERROR', {
              error: result.error
            }));
          }
          this.loading = false;
        },
        error => {
          this.notification.showError(this.fErrorFetch);
          this.loading = false;
        }
    );
  }
}
